/**
 * Accessible vehicle table view for screen readers.
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { debounce } from 'lodash-es';

import { useVehicleStore } from '../../stores/vehicleStore';
import { useFilterStore } from '../../stores/filterStore';
import { A11Y_CONFIG } from '../../config';
import { formatDataAge } from '../../utils/confidence';
import { DataFreshnessCell } from './DataFreshnessCell';
import type { VehicleState } from '../../stores/vehicleStore';

export interface VehicleTableProps {
  /** Additional CSS class */
  className?: string;
}

type SortField = 'line' | 'journeyGid' | 'status' | 'v1Freshness' | 'v4Freshness' | 'speed' | 'updated';
type SortDirection = 'asc' | 'desc';

// SortHeader component defined outside VehicleTable
interface SortHeaderProps {
  field: SortField;
  currentField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  children: React.ReactNode;
}

function SortHeader({
  field,
  currentField,
  sortDirection,
  onSort,
  children,
}: SortHeaderProps) {
  return (
    <th
      scope="col"
      className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
      onClick={() => onSort(field)}
      onKeyDown={(e) => e.key === 'Enter' && onSort(field)}
      tabIndex={0}
      aria-sort={
        currentField === field
          ? sortDirection === 'asc'
            ? 'ascending'
            : 'descending'
          : 'none'
      }
    >
      <div className="flex items-center gap-1">
        {children}
        {currentField === field && (
          <span aria-hidden="true">{sortDirection === 'asc' ? '↑' : '↓'}</span>
        )}
      </div>
    </th>
  );
}

export function VehicleTable({ className = '' }: VehicleTableProps) {
  const vehicles = useVehicleStore((state) => state.vehicles);
  const selectedVehicleId = useVehicleStore((state) => state.selectedVehicleId);
  const selectVehicle = useVehicleStore((state) => state.selectVehicle);
  const enabledModes = useFilterStore((state) => state.enabledModes);
  const zoomLevel = useFilterStore((state) => state.zoomLevel);
  const lineFilters = useFilterStore((state) => state.lineFilters);
  const getVisibleModes = useFilterStore((state) => state.getVisibleModes);
  const hasLineFilter = useFilterStore((state) => state.hasLineFilter);

  const [sortField, setSortField] = useState<SortField>('line');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [liveRegionContent, setLiveRegionContent] = useState('');
  // Single shared timer for all DataFreshnessCell components (avoids 1600+ timers)
  const [currentTime, setCurrentTime] = useState(() => Date.now());

  const tableRef = useRef<HTMLTableElement>(null);
  const liveRegionRef = useRef<HTMLDivElement>(null);

  // Single timer to update currentTime for all freshness cells
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get visible transport modes - depends on enabledModes and zoomLevel for reactivity
  const visibleModes = useMemo(() => getVisibleModes(), [enabledModes, zoomLevel, getVisibleModes]);

  // Filter and sort vehicles
  const sortedVehicles = useMemo(() => {
    const filtered = Array.from(vehicles.values()).filter((v) => {
      // Mode filter
      if (!v.line || !visibleModes.includes(v.line.transport_mode)) {
        return false;
      }

      // Visual state filter
      if (v.visualState === 'dead') {
        return false;
      }

      // Line filter - if lineFilters is empty, show all; otherwise check exact match
      const lineName = v.line?.short_name ?? '';
      if (!hasLineFilter(lineName)) {
        return false;
      }

      return true;
    });

    return filtered.sort((a, b) => {
      let comparison = 0;

      switch (sortField) {
        case 'line':
          comparison = (a.line?.short_name ?? '').localeCompare(
            b.line?.short_name ?? ''
          );
          break;
        case 'journeyGid':
          comparison = a.journey_gid.localeCompare(b.journey_gid);
          break;
        case 'status': {
          // Sort by data source (GPS first) then by at_stop (moving < at stop)
          const aIsGps = a.data_source === 'v1-da-gps' ? 0 : 1;
          const bIsGps = b.data_source === 'v1-da-gps' ? 0 : 1;
          comparison = aIsGps - bIsGps;
          if (comparison === 0) {
            comparison = (a.at_stop ? 1 : 0) - (b.at_stop ? 1 : 0);
          }
          break;
        }
        case 'v1Freshness': {
          // Sort by V1 (GPS) freshness - most recent first, null values last
          const aTime = a.v1LastUpdateTime ?? 0;
          const bTime = b.v1LastUpdateTime ?? 0;
          comparison = bTime - aTime;
          break;
        }
        case 'v4Freshness': {
          // Sort by V4 (Estimated) freshness - most recent first, null values last
          const aTime = a.v4LastUpdateTime ?? 0;
          const bTime = b.v4LastUpdateTime ?? 0;
          comparison = bTime - aTime;
          break;
        }
        case 'speed':
          comparison = (a.speed_kmh ?? 0) - (b.speed_kmh ?? 0);
          break;
        case 'updated':
          comparison = b.lastUpdateTime - a.lastUpdateTime;
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [vehicles, visibleModes, sortField, sortDirection, lineFilters, hasLineFilter]);

  // Handle sort column click
  const handleSort = useCallback(
    (field: SortField) => {
      if (field === sortField) {
        // Same column - toggle direction
        setSortDirection((dir) => (dir === 'asc' ? 'desc' : 'asc'));
      } else {
        // Different column - set new field and reset to ascending
        setSortField(field);
        setSortDirection('asc');
      }
    },
    [sortField]
  );

  // Handle row selection
  const handleRowClick = useCallback(
    (id: string) => {
      selectVehicle(id === selectedVehicleId ? null : id);
    },
    [selectVehicle, selectedVehicleId]
  );

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, vehicle: VehicleState) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        handleRowClick(vehicle.service_journey_id);
      }
    },
    [handleRowClick]
  );

  // Debounced live region updates (max every 5s)
  const updateLiveRegion = useMemo(
    () =>
      debounce((count: number) => {
        setLiveRegionContent(`${count} vehicles currently displayed`);
      }, A11Y_CONFIG.UPDATE_DEBOUNCE_MS),
    []
  );

  // Update live region when vehicle count changes
  useEffect(() => {
    updateLiveRegion(sortedVehicles.length);
    return () => updateLiveRegion.cancel();
  }, [sortedVehicles.length, updateLiveRegion]);

  return (
    <div className={`overflow-hidden ${className}`}>
      {/* Live region for screen reader announcements */}
      <div
        ref={liveRegionRef}
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
      >
        {liveRegionContent}
      </div>

      {/* Skip link */}
      <a
        href="#vehicle-table"
        className="skip-link"
      >
        Skip to vehicle table
      </a>

      <div className="overflow-x-auto">
        <table
          ref={tableRef}
          id="vehicle-table"
          className="min-w-full divide-y divide-gray-200 dark:divide-gray-700"
          role="grid"
          aria-label="Vehicle positions"
          aria-rowcount={sortedVehicles.length}
        >
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <SortHeader
                field="line"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Line
              </SortHeader>
              <SortHeader
                field="journeyGid"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Journey GID
              </SortHeader>
              <SortHeader
                field="status"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Status
              </SortHeader>
              <SortHeader
                field="v1Freshness"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                V1 (GPS)
              </SortHeader>
              <SortHeader
                field="v4Freshness"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                V4 (Est)
              </SortHeader>
              <SortHeader
                field="speed"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Speed
              </SortHeader>
              <SortHeader
                field="updated"
                currentField={sortField}
                sortDirection={sortDirection}
                onSort={handleSort}
              >
                Last Updated
              </SortHeader>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {sortedVehicles.map((vehicle, index) => (
              <tr
                key={vehicle.service_journey_id}
                className={`
                  cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors
                  ${selectedVehicleId === vehicle.service_journey_id ? 'bg-blue-50 dark:bg-blue-900/30' : ''}
                `}
                onClick={() => handleRowClick(vehicle.service_journey_id)}
                onKeyDown={(e) => handleKeyDown(e, vehicle)}
                tabIndex={0}
                role="row"
                aria-rowindex={index + 2}
                aria-selected={selectedVehicleId === vehicle.service_journey_id}
              >
                <td className="px-4 py-3 whitespace-nowrap">
                  <div
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium"
                    style={{
                      backgroundColor: vehicle.line?.color_bg || '#666666',
                      color: vehicle.line?.color_fg || '#ffffff',
                    }}
                  >
                    {vehicle.line?.short_name ?? '?'}
                  </div>
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {vehicle.journey_gid}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  {(() => {
                    const isGps = vehicle.data_source === 'v1-da-gps';
                    const colorClass = isGps
                      ? 'text-green-600 dark:text-green-400'
                      : 'text-yellow-600 dark:text-yellow-400';
                    const dotClass = isGps ? 'bg-green-500' : 'bg-yellow-500';
                    const label = isGps ? 'GPS' : 'Estimated';
                    return (
                      <span className={`inline-flex items-center gap-1 text-sm ${colorClass}`}>
                        <span className={`w-2 h-2 rounded-full ${dotClass}`} aria-hidden="true" />
                        {label}
                        {vehicle.at_stop && ' (At Stop)'}
                      </span>
                    );
                  })()}
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <DataFreshnessCell
                    key={`${vehicle.service_journey_id}-v1`}
                    lastUpdateTime={vehicle.v1LastUpdateTime}
                    currentTime={currentTime}
                    label="GPS"
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap">
                  <DataFreshnessCell
                    key={`${vehicle.service_journey_id}-v4`}
                    lastUpdateTime={vehicle.v4LastUpdateTime}
                    currentTime={currentTime}
                    label="Estimated"
                  />
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                  {vehicle.speed_kmh !== null ? `${vehicle.speed_kmh.toFixed(0)} km/h` : 'N/A'}
                </td>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                  {formatDataAge(vehicle.lastUpdateTime, currentTime)}
                </td>
              </tr>
            ))}

            {sortedVehicles.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No vehicles to display. Adjust your filters or wait for data.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Vehicle count */}
      <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 text-sm text-gray-500 dark:text-gray-400">
        {sortedVehicles.length} vehicle{sortedVehicles.length !== 1 ? 's' : ''} displayed
      </div>
    </div>
  );
}
